import React from "react";
import { Link } from "react-router-dom";

const sectionClass = "space-y-3";
const h2Class = "text-xl font-semibold text-white scroll-mt-24";
const pClass = "text-sm leading-relaxed text-gray-300";
const ulClass = "list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-300";

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-300">
      <header className="sticky top-0 z-50 border-b border-gray-800 bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-gray-950/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <span className="text-lg font-semibold tracking-tight text-white">IntelliWarrior</span>
          <Link
            to="/"
            className="text-sm font-medium text-emerald-400 transition-colors hover:text-emerald-300"
          >
            Back to Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 pb-16 sm:px-6">
        <div className="mb-10 border-b border-gray-800 pb-8">
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
          <p className="mt-2 text-sm text-gray-400">
            IntelliWarrior is operated by Warrior Financial Services LLC (&quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;). By accessing or using IntelliWarrior, you agree to these Terms of Service
            (&quot;Terms&quot;). If you do not agree, do not use the Service.
          </p>
          <p className="mt-2 text-xs text-gray-500">Last updated: April 1, 2026</p>
        </div>

        <div className="space-y-10">
          <section id="agreement" className={sectionClass}>
            <h2 className={h2Class}>1. Agreement to Terms</h2>
            <p className={pClass}>
              These Terms constitute a binding agreement between you and Warrior Financial Services LLC
              regarding your use of the IntelliWarrior platform, websites, and related services
              (collectively, the &quot;Service&quot;). You represent that you have the legal capacity to enter
              into this agreement and, if you are using the Service on behalf of an organization, that you
              have authority to bind that organization.
            </p>
          </section>

          <section id="description" className={sectionClass}>
            <h2 className={h2Class}>2. Description of Service</h2>
            <p className={pClass}>
              IntelliWarrior is a software platform designed to help credit repair businesses and related
              professionals (&quot;Operators&quot;) manage clients, disputes, documents, communications,
              billing, and optional AI-assisted workflows. The Service does not provide legal, financial, or
              credit advice. Features may change over time; we may add, modify, or discontinue functionality
              with reasonable notice where practicable.
            </p>
          </section>

          <section id="eligibility" className={sectionClass}>
            <h2 className={h2Class}>3. Eligibility</h2>
            <p className={pClass}>
              You must be at least eighteen (18) years old and able to form a binding contract. You may not
              use the Service if you are prohibited from doing so under applicable law or if we have
              suspended or terminated your account. You are responsible for ensuring that your use of the
              Service complies with all laws and regulations that apply to you and your business.
            </p>
          </section>

          <section id="account" className={sectionClass}>
            <h2 className={h2Class}>4. Account Registration</h2>
            <p className={pClass}>
              You must provide accurate, current, and complete registration information and keep it
              updated. You are responsible for safeguarding credentials and for all activity under your
              account. Notify us promptly at{" "}
              <a href="mailto:info@intelliwarrior.com" className="text-emerald-400 hover:underline">
                info@intelliwarrior.com
              </a>{" "}
              if you suspect unauthorized access. We may suspend or terminate accounts that violate these
              Terms or pose security or legal risk.
            </p>
          </section>

          <section id="subscription" className={sectionClass}>
            <h2 className={h2Class}>5. Subscription Plans and Payment</h2>
            <p className={pClass}>
              Access to paid features is offered on a subscription basis. Unless otherwise stated at signup
              or checkout:
            </p>
            <ul className={ulClass}>
              <li>
                <strong className="text-gray-200">Free trial:</strong> New subscribers may receive a
                fourteen (14) day free trial. Trial terms, eligible plans, and conversion to a paid
                subscription are as disclosed at signup. If you do not cancel before the trial ends, your
                subscription may convert to a paid plan and your payment method may be charged.
              </li>
              <li>
                <strong className="text-gray-200">Tiers:</strong> Plans may include Starter, Growth, and
                Agency (or similarly named) tiers with different limits and features as described on our
                pricing page or in your order summary.
              </li>
              <li>
                <strong className="text-gray-200">Billing:</strong> Subscriptions are billed monthly in
                advance unless another billing cycle is stated. Fees are non-refundable except as required by
                law or as expressly stated in writing by us.
              </li>
              <li>
                <strong className="text-gray-200">Cancellation:</strong> You may cancel your subscription at
                any time through the billing settings or by contacting us. Cancellation stops future
                charges; access may continue until the end of the current billing period.
              </li>
            </ul>
            <p className={pClass}>
              Payment processing may be handled by third-party providers (e.g., Stripe). Their terms and
              privacy practices also apply.
            </p>
          </section>

          <section id="operator" className={sectionClass}>
            <h2 className={h2Class}>6. Operator Responsibilities</h2>
            <p className={pClass}>
              If you use IntelliWarrior in connection with credit repair or related services, you are solely
              responsible for compliance with applicable laws and regulations, including without limitation
              the Credit Repair Organizations Act (CROA), the Fair Credit Reporting Act (FCRA), and the Fair
              Debt Collection Practices Act (FDCPA), where applicable. You agree that you will not use the
              Service in a manner that violates such laws.
            </p>
            <ul className={ulClass}>
              <li>
                You will obtain and maintain any required disclosures, contracts, and authorizations from
                your clients as required by law.
              </li>
              <li>
                You will not charge illegal or prohibited fees (for example, CROA restricts certain upfront
                fees for credit repair services to consumers).
              </li>
              <li>
                You will not guarantee specific credit outcomes, score increases, or removal of information
                from credit reports unless permitted by law and supported by truthful, substantiated
                communications.
              </li>
            </ul>
            <p className={pClass}>
              Nothing in these Terms or in the Service constitutes legal advice. Consult qualified counsel for
              compliance with laws that apply to your business.
            </p>
          </section>

          <section id="ai" className={sectionClass}>
            <h2 className={h2Class}>7. AI-Powered Features</h2>
            <p className={pClass}>
              Certain features may use artificial intelligence or machine learning to assist with drafting,
              summarization, or suggestions. AI output is informational and assistive only. It is not legal,
              financial, or credit advice and may be incomplete or inaccurate. You are responsible for
              reviewing, editing, and approving any content before use with clients, regulators, or third
              parties. Do not rely on AI output as a substitute for professional judgment or legal counsel.
            </p>
          </section>

          <section id="white-label" className={sectionClass}>
            <h2 className={h2Class}>8. White-Label Branding</h2>
            <p className={pClass}>
              If your plan includes white-label or custom branding features, you may display your own
              branding subject to your plan limits and our technical requirements. You grant us a limited
              license to host and display your trademarks and content solely to provide the Service. You
              represent that you have the rights to any branding and content you upload and that they do not
              infringe third-party rights.
            </p>
          </section>

          <section id="privacy" className={sectionClass}>
            <h2 className={h2Class}>9. Data and Privacy</h2>
            <p className={pClass}>
              Our collection and use of personal information is described in our Privacy Policy. Please
              review it at{" "}
              <Link to="/privacy" className="text-emerald-400 hover:underline">
                /privacy
              </Link>
              . By using the Service, you acknowledge our privacy practices as described there.
            </p>
          </section>

          <section id="ip" className={sectionClass}>
            <h2 className={h2Class}>10. Intellectual Property</h2>
            <p className={pClass}>
              The Service, including software, designs, text, graphics, logos, and documentation, is owned
              by Warrior Financial Services LLC or its licensors and is protected by intellectual property
              laws. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable,
              revocable license to use the Service for your internal business purposes during your
              subscription. You may not copy, modify, distribute, sell, lease, reverse engineer, or attempt
              to extract source code except as permitted by law.
            </p>
          </section>

          <section id="liability" className={sectionClass}>
            <h2 className={h2Class}>11. Limitation of Liability</h2>
            <p className={pClass}>
              To the maximum extent permitted by law, Warrior Financial Services LLC and its affiliates,
              officers, directors, employees, and agents will not be liable for any indirect, incidental,
              special, consequential, or punitive damages, or any loss of profits, data, goodwill, or
              business opportunities, arising from or related to your use of the Service, even if advised of
              the possibility of such damages.
            </p>
            <p className={pClass}>
              Our aggregate liability for any claim arising out of or relating to the Service or these Terms
              will not exceed the greater of (a) the amounts you paid us for the Service in the twelve (12)
              months before the claim, or (b) one hundred U.S. dollars (USD $100), except where prohibited by
              law.
            </p>
          </section>

          <section id="warranties" className={sectionClass}>
            <h2 className={h2Class}>12. Disclaimer of Warranties</h2>
            <p className={pClass}>
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY
              KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
              SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
            </p>
          </section>

          <section id="indemnification" className={sectionClass}>
            <h2 className={h2Class}>13. Indemnification</h2>
            <p className={pClass}>
              You will defend, indemnify, and hold harmless Warrior Financial Services LLC and its
              affiliates, officers, directors, employees, and agents from and against any claims,
              liabilities, damages, losses, and expenses (including reasonable attorneys&apos; fees) arising
              out of or related to your use of the Service, your violation of these Terms, your violation of
              law, or your content or data (including client data you process through the Service).
            </p>
          </section>

          <section id="termination" className={sectionClass}>
            <h2 className={h2Class}>14. Termination</h2>
            <p className={pClass}>
              You may stop using the Service at any time. We may suspend or terminate your access if you
              breach these Terms, if we are required to do so by law, or if we discontinue the Service.
              Provisions that by their nature should survive termination (including intellectual property,
              disclaimers, limitation of liability, indemnification, and governing law) will survive.
            </p>
          </section>

          <section id="governing-law" className={sectionClass}>
            <h2 className={h2Class}>15. Governing Law</h2>
            <p className={pClass}>
              These Terms are governed by the laws of the State of Illinois, without regard to conflict of
              law principles. You agree that exclusive jurisdiction and venue for any dispute arising out of
              or relating to these Terms or the Service will be the state and federal courts located in Cook
              County, Illinois, subject to applicable law.
            </p>
          </section>

          <section id="changes" className={sectionClass}>
            <h2 className={h2Class}>16. Changes to Terms</h2>
            <p className={pClass}>
              We may modify these Terms from time to time. We will post the updated Terms on this page and
              update the &quot;Last updated&quot; date. Material changes may be communicated by email or
              in-product notice where appropriate. Your continued use of the Service after changes become
              effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section id="contact" className={sectionClass}>
            <h2 className={h2Class}>17. Contact</h2>
            <p className={pClass}>
              Questions about these Terms? Contact Warrior Financial Services LLC at{" "}
              <a href="mailto:info@intelliwarrior.com" className="text-emerald-400 hover:underline">
                info@intelliwarrior.com
              </a>
              .
            </p>
          </section>
        </div>

        <footer className="mt-16 border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Warrior Financial Services LLC. All rights reserved.
        </footer>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
